import React from 'react'
import { useParams, Navigate, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import Header from '../components/header'
import { headerProps } from '../components/HeaderConfig'
import Footer from '../components/footer'
import ThemeToggle from '../components/theme-toggle'
import useTheme from '../hooks/useTheme'
import { getArticleBySlug, articlesData } from '../data/articles'
import './article-detail.css'

const ArticleDetail = () => {
  const { isDark, toggleTheme } = useTheme()
  const { slug } = useParams()
  const article = getArticleBySlug(slug)

  if (!article) {
    return <Navigate to="/articles" replace />
  }

  const formattedDate = new Date(article.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const words = article.content ? article.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0
  const readTime = `${Math.max(3, Math.ceil(words / 200))} min read`

  return (
    <div className="artd-container">
      <Helmet>
        <title>{article.title} | Abhishek Palit</title>
        <meta name="description" content={article.excerpt} />
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://apalit.net/articles/${slug}`} />
        <meta property="og:title" content={`${article.title} | Abhishek Palit`} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:image" content={`https://apalit.net${article.imageUrl}`} />
        <meta property="og:site_name" content="Abhishek Palit" />
        <meta property="article:published_time" content={article.date} />
        <meta property="article:author" content="Abhishek Palit" />
        <meta property="article:section" content={article.category} />
        {/* Twitter / X */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${article.title} | Abhishek Palit`} />
        <meta name="twitter:description" content={article.excerpt} />
        <meta name="twitter:image" content={`https://apalit.net${article.imageUrl}`} />
      </Helmet>

      <Header
        {...headerProps}
        rootClassName="headerroot-class-name"
        themeToggle={<ThemeToggle isDark={isDark} toggleTheme={toggleTheme} />}
      />

      <article className="artd-page">
        {/* Back link */}
        <Link to="/articles" className="artd-back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          All articles
        </Link>

        {/* Article Header */}
        <header className="artd-header">
          <div className="artd-meta">
            <span className="artd-category">{article.category}</span>
            <span className="artd-dot">·</span>
            <span className="artd-date">{formattedDate}</span>
            <span className="artd-dot">·</span>
            <span className="artd-read-time">{readTime}</span>
          </div>
          <h1 className="artd-title">{article.title}</h1>
          <p className="artd-excerpt">{article.excerpt}</p>

          {/* Author */}
          <div className="artd-author">
            <img src="/abhishekimg-500w.jpeg" alt="Abhishek Palit" className="artd-author-img" />
            <div>
              <span className="artd-author-name">Abhishek Palit</span>
              <span className="artd-author-role">VDC Assistant Engineer · Turner Construction</span>
            </div>
          </div>
        </header>

        {/* Hero Image */}
        {article.imageUrl && (
          <div className="artd-hero-image">
            <img src={article.imageUrl} alt={article.title} />
            {article.imageCaption && (
              <span className="artd-image-caption">{article.imageCaption}</span>
            )}
          </div>
        )}

        {/* Article Body */}
        <div className="artd-body" dangerouslySetInnerHTML={{ __html: article.content }} />

        {/* Tags & Share */}
        <div className="artd-tags-share">
          <div className="artd-tags">
            <span className="artd-tag">{article.category}</span>
            <span className="artd-tag">Construction Tech</span>
          </div>
          <div className="artd-share">
            <span className="artd-share-label">Share</span>
            <button
              onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank', 'width=600,height=500')}
              className="artd-share-btn"
              aria-label="Share on LinkedIn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </button>
            <button
              onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(window.location.href)}`, '_blank', 'width=600,height=400')}
              className="artd-share-btn"
              aria-label="Share on X"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Footer meta */}
        <div className="artd-footer-meta">
          <span className="artd-published">Published {formattedDate}</span>
          <Link to="/articles" className="artd-back-bottom">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to articles
          </Link>
        </div>

        {/* Related Articles */}
        {articlesData.filter(a => a.slug !== slug).length > 0 && (
          <div className="artd-related">
            <h3 className="artd-related-title">Continue Reading</h3>
            <div className="artd-related-grid">
              {articlesData.filter(a => a.slug !== slug).slice(0, 2).map((related) => (
                <Link key={related.id} to={`/articles/${related.slug}`} className="artd-related-card">
                  {related.imageUrl && (
                    <div className="artd-related-image">
                      <img src={related.imageUrl} alt={related.title} />
                    </div>
                  )}
                  <div className="artd-related-content">
                    <span className="artd-related-category">{related.category}</span>
                    <h4 className="artd-related-heading">{related.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      <Footer rootClassName="footerroot-class-name" />
    </div>
  )
}

export default ArticleDetail
